import type { Comment } from '@billing/validation/comment';
import {
  ActionIcon,
  Card,
  Group,
  Menu,
  Stack,
  Text,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { IconDotsVertical, IconTrash } from '@tabler/icons-react';
import { DateTime } from 'luxon';

const formatCommentTime = (createdAt: string, timezone: string): string => {
  const parsed = DateTime.fromISO(createdAt, { setZone: true });
  const timestamp = parsed.isValid
    ? parsed
    : DateTime.fromSQL(createdAt, { setZone: true });
  return timestamp.isValid
    ? timestamp.setZone(timezone).toLocaleString(DateTime.DATETIME_MED)
    : '';
};

export const ClaimCommentMessage = ({
  comment,
  timezone,
  onDelete,
}: {
  comment: Comment;
  timezone: string;
  onDelete?: () => void;
}) => {
  const { translate } = useTranslation();
  const isDark = useComputedColorScheme('dark') === 'dark';
  const nameParts = [
    comment.author?.firstName,
    comment.author?.lastName,
  ].filter((part): part is string => Boolean(part?.trim()));
  const author =
    nameParts.join(' ') ||
    comment.author?.email ||
    translate('pages:claims.comments.unknownAuthor', 'Unknown author');
  const createdAt = formatCommentTime(comment.createdAt, timezone);

  return (
    <Card
      role="listitem"
      radius="md"
      p="sm"
      w="fit-content"
      maw={480}
      style={{ backgroundColor: 'var(--mantine-color-default-hover)' }}
    >
      <Stack gap={4}>
        <Group justify="space-between" gap="xs" align="center">
          <Text
            size="sm"
            fw={600}
            c={isDark ? '#fff' : 'var(--mantine-color-text)'}
            style={{ lineHeight: '20px', overflowWrap: 'anywhere' }}
          >
            {author}
          </Text>
          <Group gap={4} align="center" wrap="nowrap">
            <Text
              size="xs"
              c="dimmed"
              style={{ lineHeight: '20px', whiteSpace: 'nowrap' }}
            >
              {createdAt ||
                translate('pages:claims.comments.unknownTime', 'Unknown time')}
            </Text>
            {onDelete && (
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size={20}
                    aria-label={translate(
                      'pages:claims.comments.actions',
                      'Comment actions'
                    )}
                  >
                    <IconDotsVertical size={14} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    color="red"
                    leftSection={<IconTrash size={16} />}
                    onClick={onDelete}
                  >
                    {translate(
                      'pages:claims.comments.delete',
                      'Delete comment'
                    )}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Group>
        <Text
          size="sm"
          fw={400}
          c={isDark ? '#ECECEC' : 'var(--mantine-color-text)'}
          style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
        >
          {comment.text}
        </Text>
      </Stack>
    </Card>
  );
};
